import { getClientEnvironmentDetails } from '@sm/utils';
import {
  DigitalData,
  DigitalDataEvent,
  MetricsTrackerType,
  MetricsTrackerConfigurationType,
  MetricsTrackerUpdateConfigurationType,
  MetricsTrackerRoot,
} from './types/types';
import USER_EVENTS from './types/enums';
import getCookieValue from './utils';
import { pageViewMutation, maskEmail } from './helpers';

import dataAnalyticsMetricsSubscriber from './subscribers/DataAnalyticsMetricsSubscriber';
import loggingMetricsSubscriber from './subscribers/LoggingMetricsSubscriber';
import gtmMetricsSubscriber from './subscribers/GTMMeticsSubscriber';
import facebookPixelSubscriber from './subscribers/FacebookPixelSubscriber';
import mixPanelSubscriber from './subscribers/MixPanelSubscriber';

const { isBrowser } = getClientEnvironmentDetails();

// Import/require only in the browser
const delegatedEvents = isBrowser ? require('delegated-events') : null;

function getCleanDigitalData(userId: string = '1'): DigitalData {
  return {
    page: {
      pageInfo: {
        pageId: '',
        attributes: {},
        pathname: '',
      },
    },
    events: [],
    components: [],
    user: {
      ep201: getCookieValue('ep201'),
      ep202: getCookieValue('ep202'),
      id: userId,
    },
  };
}

export function generateMetricsAttribute(eventData: {
  name?: USER_EVENTS;
  data: Record<string, any>;
}): String {
  return JSON.stringify({
    name: eventData.name || USER_EVENTS.ELEMENT_INTERACTION,
    data: eventData.data,
  });
}

const GLOBAL_KEY = 'sm_metrics_tracker_do_not_touch_this';
const glob: Record<string, any> = isBrowser ? window : global;

function setupRoot(): MetricsTrackerRoot {
  if (!glob[GLOBAL_KEY]) {
    const root: MetricsTrackerRoot = {
      subscribers: [],
      automaticSubscribers: [],
      config: {
        user: { id: '', isAuthenticated: false },
        dataAnalyticsAPIPath: null,
        loggingAPIPath: null,
        country: 'GB',
        legacyWeb: '',
        gtmId: '',
      },
      digitalData: getCleanDigitalData(),
    };
    glob[GLOBAL_KEY] = root;
  }
  return glob[GLOBAL_KEY] as MetricsTrackerRoot;
}

function getRoot(): MetricsTrackerRoot {
  if (!glob[GLOBAL_KEY]) {
    throw new Error(
      'Please initialize MetricsTracker with a userId before tracking events.'
    );
  }
  return glob[GLOBAL_KEY];
}

export const MetricsTracker: MetricsTrackerType = {
  initialize(config: MetricsTrackerConfigurationType): MetricsTrackerRoot {
    const root = setupRoot();
    root.config = config;
    root.digitalData = getCleanDigitalData(config.user.id);
    setupEvents();
    return root;
  },

  reset(): MetricsTrackerRoot {
    const root = getRoot();
    root.digitalData = getCleanDigitalData(root.config.user.id);
    root.subscribers = [];
    root.automaticSubscribers = [];

    // Add the mandatory dataAnalytics subscriber
    this.addSubscriber(dataAnalyticsMetricsSubscriber);
    // Add the mandatory logging subscriber
    this.addSubscriber(loggingMetricsSubscriber);
    // Add Google Tag Manager dataLayer subscriber
    MetricsTracker.addSubscriber(gtmMetricsSubscriber);
    // Add Facebook Pixel subscriber
    MetricsTracker.addSubscriber(facebookPixelSubscriber);
    // Add MixPanel Subscriber
    MetricsTracker.addSubscriber(mixPanelSubscriber);
    // Add MixPanel Automatic Subscriber
    MetricsTracker.addAutomaticSubscriber(mixPanelSubscriber);

    return root;
  },

  update(config: MetricsTrackerUpdateConfigurationType): MetricsTrackerRoot {
    // for some data points within an SPA, the configuration must be
    // updated post initialization
    const root = getRoot();
    root.config = { ...root.config, ...config };
    return root;
  },

  getSubscribers() {
    const { subscribers } = getRoot();
    return subscribers;
  },

  getAutomaticSubscribers() {
    const { automaticSubscribers } = getRoot();
    return automaticSubscribers;
  },

  track(event: DigitalDataEvent) {
    const root = getRoot();
    if (root.config.user.id === '') {
      throw new Error(
        'Please initialize MetricsTracker with a userId before tracking events.'
      );
    }
    const maskedEvent = event;

    // mask email addresses
    if (event?.data?.email) {
      maskedEvent.data.email = maskEmail(event.data.email);
    }

    if (event?.data?.data?.email) {
      maskedEvent.data.data.email = maskEmail(event.data.data.email);
    }

    if (USER_EVENTS[event.name]) {
      root.digitalData.events.push(event);

      if (USER_EVENTS.COMPONENT_ADD === event.name) {
        root.digitalData.components.push(event.data);
      }

      if (
        event.name === USER_EVENTS.PAGE_VIEW ||
        event.name === USER_EVENTS.VIRTUAL_PAGE_VIEW
      ) {
        root.digitalData = pageViewMutation(event, root.digitalData);
      }

      root.subscribers.forEach(subscriber => {
        subscriber(event, root.config);
      });
    }
  },

  automaticTrack(event: DigitalDataEvent) {
    const root = getRoot();
    root.automaticSubscribers.forEach(subscriber => {
      subscriber(event, root.config);
    });
  },

  addSubscriber(
    subscriber: (
      digitalDataEvent: DigitalDataEvent,
      metricsTrackerConfig: MetricsTrackerConfigurationType
    ) => void
  ): void {
    /**
     * We always want to allow the subscribers to subscribe. If the user
     * did not accept GDPR, we don't fire events; hence, we never violate GDPR.
     * If, however, the user does allow tracking, we don't want to subscribers
     * to re-subscribe.
     */
    const root = getRoot();
    root.subscribers.push(subscriber);
  },
  addAutomaticSubscriber(
    subscriber: (
      digitalDataEvent: DigitalDataEvent,
      metricsTrackerConfig: MetricsTrackerConfigurationType
    ) => void
  ): void {
    const root = getRoot();
    root.automaticSubscribers.push(subscriber);
  },
  getConfig(): MetricsTrackerConfigurationType {
    return getRoot().config;
  },
  getDigitalData(): DigitalData {
    return getRoot().digitalData;
  },
};

function setupEvents() {
  if (!isBrowser) {
    return;
  }

  if (window && window.SM && window.SM.MetricsTracker) {
    // only create one event listener on the document
    // this makes it safe for consumer apps to call initialize multiple times
    return;
  }

  /**
   * Add on click event listener
   * Note - this is done in the getInstance method; hence, until this
   * method is called, the event handler would never be bound.
   */
  delegatedEvents.on('click', '[data-sm-metrics]', function gce(
    event: MouseEvent
  ) {
    if (event && event.currentTarget) {
      const metricsData = (event.currentTarget as HTMLElement).getAttribute(
        'data-sm-metrics'
      );
      if (metricsData) {
        const metricsDataJSON = JSON.parse(metricsData);
        const eventData = metricsDataJSON.data;

        MetricsTracker.track({
          name: metricsDataJSON.name || USER_EVENTS.ELEMENT_INTERACTION,
          data: eventData,
        });
      }
    }
  });
  // Add the mandatory dataAnalytics subscriber
  MetricsTracker.addSubscriber(dataAnalyticsMetricsSubscriber);
  // Add the mandatory logging subscriber
  MetricsTracker.addSubscriber(loggingMetricsSubscriber);
  // Add Google Tag Manager dataLayer subscriber
  MetricsTracker.addSubscriber(gtmMetricsSubscriber);
  // Add Facebook Pixel subscriber
  MetricsTracker.addSubscriber(facebookPixelSubscriber);
  // Add MixPanel Subscriber
  MetricsTracker.addSubscriber(mixPanelSubscriber);

  setupAutomaticEvents();

  window.SM = window.SM || {};
  window.SM.MetricsTracker = MetricsTracker;
}

function setupAutomaticEvents() {
  const clickableLinks = [
    '.wds-button',
    '.wds-link',
    'a',
    'wds-switch__native',
    'wds-checkbox__native',
    'wds-radio__native',
    'wds-list__item',
    'wds-close',
    'wds-popout__trigger',
    'wds-has-menu',
    'wds-slider__handle',
    'wds-tab__tab-btn',
  ];

  delegatedEvents.on('click', clickableLinks.join(','), function gce(
    event: MouseEvent
  ) {
    if (!event.target) {
      return;
    }

    const name = event.type;
    const target = event.target as HTMLElement;
    const inputTarget = target as HTMLInputElement;

    MetricsTracker.automaticTrack({
      name: USER_EVENTS.ELEMENT_INTERACTION,
      data: {
        actionType: name,
        actionFlow: 'automatic',
        class: target.className,
        text: target.innerText,
        checked: inputTarget.checked || undefined,
        value: inputTarget.value || undefined,
      },
    });
  });

  // Add MixPanel Subscriber
  MetricsTracker.addAutomaticSubscriber(mixPanelSubscriber);
}
